import { useSetRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import useApiClient from '~/src/hooks/useApiClient';
import { snackbarMessageSelector } from '~/src/Snackbar/store';
import {
  Entry,
  pendingMoveToFolder,
  pendingProjectUpdateState,
  ProjectListResults,
  projectListState,
} from '../store';
import { Project } from '~/src/Project/store';

interface ProjectWithFolderArray extends Project {
  folderArray?: number[];
}

const useProjectUpdate = () => {
  const { t } = useTranslation('projects');
  const apiClient = useApiClient();
  const setPendingFolderChange = useSetRecoilState(pendingMoveToFolder);
  const setPending = useSetRecoilState(pendingProjectUpdateState);
  const setSnackbarMessage = useSetRecoilState(snackbarMessageSelector);
  const setEntries = useSetRecoilState(projectListState);

  const updateProject = async (
    projectId: number,
    body: Partial<Project>,
    errorMessage?: string,
  ) => {
    setPending(true);
    if (body.folder === null || body.folder) {
      setPendingFolderChange(projectId);
    }
    try {
      await apiClient.patch<Project>(`/projects/${projectId}`, body);
      const params = { filter: btoa(JSON.stringify({ project: projectId })) };
      const {
        data: { data },
      } = await apiClient.get<{ data: Entry[] }>('/search', { params });
      setEntries(
        (state) => ({
          ...state,
          entries: state.entries.reduce((acc, item) => {
            if (!item.project || item.project._id !== projectId) {
              acc.push(item);
              return acc;
            }
            if (typeof body.folder !== 'undefined') {
              // Check if project should be removed from current view
              if (body.folder === null) {
                // Moving to root - remove if in any folder
                const project = item.project as ProjectWithFolderArray;
                if (
                  project.folder
                    || (project.folderArray && project.folderArray.length > 0)
                ) {
                  return acc;
                }
              } else {
                // Moving to a folder - keep if in that folder
                const folderId = typeof body.folder === 'number'
                  ? body.folder
                  : (body.folder as any)._id;
                const project = item.project as ProjectWithFolderArray;
                if (!project.folderArray?.includes(folderId)) {
                  return acc;
                }
              }
            }
            if (data && data.length > 0 && data[0]) {
              acc.push(data[0]);
            }
            return acc;
          }, []),
        } as ProjectListResults),
      );
    } catch (err) {
      console.error(err);
      setSnackbarMessage({
        message: errorMessage || t('updateProject.errors.updateFolder'),
        severity: 'error',
      });
      throw err;
    } finally {
      setPending(false);
      setPendingFolderChange(null);
    }
  };

  return [updateProject];
};

export default useProjectUpdate;
